import { Component, EventEmitter, inject, OnInit, Output } from '@angular/core';
import { FormsModule } from '@angular/forms';
import {
  UserNotificationSetting,
  DeepRootsModes,
  UserSettings,
} from '../../../../shared/models/user-settings';
import { UserSettingsService } from '../../../../shared/services/user-settings/user-settings.service';
import { takeUntil } from 'rxjs';
import { DestroyBase } from '../../../../shared/base-classes/destroy-component-base';
import { DndEmailService } from '../../../configs/dnd-list/services/dnd-email.service';
import { PermissionCode } from '../../../../shared/enums/permission-code';
import { AuthService } from '../../../auth/services/auth.service';

@Component({
  selector: 'app-notification-settings',
  standalone: true,
  imports: [FormsModule],
  templateUrl: './notification-settings.component.html',
  styleUrl: './notification-settings.component.scss',
})
export class NotificationSettingsComponent
  extends DestroyBase
  implements OnInit
{
  userSettingsService = inject(UserSettingsService);
  private _authService = inject(AuthService);

  isLoading = true;
  isEmailInDnd = false;
  hasLeadsPermission = this._authService.hasPermission(
    PermissionCode.GET_SET_LEADS,
  );
  hasDeeprootsPermission = this._authService.hasPermission(
    PermissionCode.ACCESS_DEEPROOTS,
  );

  @Output() settingsSaved = new EventEmitter<{
    success: boolean;
    message: string;
    keepOpen?: boolean;
  }>();
  @Output() settingsClosed = new EventEmitter<void>();

  // Local copy for form binding
  localSettings: UserNotificationSetting = {
    shared_notes: false,
    record_visibility: true,
    deeproots: DeepRootsModes.ALL_SUBMISSIONS,
    daily_unread_notifications_email: false,
    share_record_email: false,
    deeproots_matched_email: false,
  };

  private originalSettings: UserNotificationSetting | null = null;
  private settingsExist = false;
  private dndEmailService = inject(DndEmailService);

  // Deep roots options for dropdown
  deepRootsOptions = [
    {
      value: DeepRootsModes.ALL_SUBMISSIONS,
      label: 'Notify me on every form submission',
    },
    {
      value: DeepRootsModes.ONLY_MATCHES,
      label: 'Only when lead criteria matches',
    },
  ];

  ngOnInit(): void {
    this.loadUserSettings();
  }

  loadUserSettings(): void {
    this.isLoading = true;
    this.userSettingsService
      .getUserSettingsByType('notification')
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          const userSettings = response as UserSettings;
          this.isEmailInDnd = userSettings?.is_dnd === true;
          if (userSettings?.settings_value) {
            this.localSettings = { ...userSettings.settings_value };
            this.originalSettings = { ...userSettings.settings_value };
            this.settingsExist = true;
          }
          this.isLoading = false;
        },
        error: (error) => {
          // Check if it's a "not found" error (404) or actual failure
          if (error.status === 404) {
            // Settings don't exist yet, use default values
            this.localSettings = {
              shared_notes: false,
              record_visibility: false,
              deeproots: DeepRootsModes.ALL_SUBMISSIONS,
              daily_unread_notifications_email: false,
              share_record_email: false,
              deeproots_matched_email: false,
            };
            this.originalSettings = null;
            this.settingsExist = false;
            this.isEmailInDnd = false;
          } else {
            // Actual error occurred - we can't emit from here as parent doesn't expect it
            console.error('Failed to load notification settings:', error);
          }
          this.isLoading = false;
        },
      });
  }

  saveSettings(): void {
    const settingsPayload: UserSettings = {
      settings_value: this.localSettings,
    };

    if (this.settingsExist) {
      // Settings exist, update them
      this.userSettingsService
        .updateUserSettingsByType('notification', settingsPayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.originalSettings = { ...this.localSettings };
            this.settingsSaved.emit({
              success: true,
              message: 'Notification settings updated successfully',
            });
          },
          error: () => {
            this.settingsSaved.emit({
              success: false,
              message: 'Failed to update notification settings',
            });
          },
        });
    } else {
      // Settings don't exist, create them
      this.userSettingsService
        .createUserSettingsByType('notification', settingsPayload)
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.originalSettings = { ...this.localSettings };
            this.settingsExist = true; // Now they exist for future saves
            this.settingsSaved.emit({
              success: true,
              message: 'Notification settings saved successfully',
            });
          },
          error: () => {
            this.settingsSaved.emit({
              success: false,
              message: 'Failed to save notification settings',
            });
          },
        });
    }
  }

  closeSettings(): void {
    // Reset to original settings if they were loaded
    if (this.originalSettings) {
      this.localSettings = { ...this.originalSettings };
    }
    this.settingsClosed.emit();
  }

  onResumeEmails(): void {
    this.dndEmailService
      .disableDnd()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.isEmailInDnd = false;
          this.settingsSaved.emit({
            success: true,
            message: 'Email notifications have been successfully resumed.',
            keepOpen: true,
          });
        },
        error: () => {
          this.settingsSaved.emit({
            success: false,
            message: 'Failed to resume email notifications.',
            keepOpen: true,
          });
        },
      });
  }
}
